import { useState, useEffect, useRef } from 'react';
import { payslipApi, userApi, PayslipStatusEmployee } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';

// Configure PDF.js worker - use unpkg for reliable CDN (must match installed version)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs`;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface User {
  userId: string;
  firstName?: string;
  lastName?: string;
  email: string;
  department?: string;
  role?: string;
}

interface PageAssignment {
  pageNumber: number;
  userId: string | null;
  employeeName: string | null;
}

export default function PayslipManagementPage() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [status, setStatus] = useState<{ summary: any; employees: PayslipStatusEmployee[] } | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // PDF Splitter state
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageAssignments, setPageAssignments] = useState<PageAssignment[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; currentEmployee: string } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i + 1);

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (pdfDoc && currentPage) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statusResponse, usersResponse] = await Promise.all([
        payslipApi.getStatus(selectedYear, selectedMonth),
        userApi.listUsers({ activeOnly: true }),
      ]);
      setStatus(statusResponse);
      // Filter out board members from users list
      const activeUsers = ((usersResponse as any).users || []).filter(
        (u: User) => u.role !== 'BOARD'
      );
      setUsers(activeUsers);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      alert('Please select a PDF file');
      return;
    }

    try {
      setPdfFile(file);
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setPdfDoc(pdf);
      setPageCount(pdf.numPages);
      setCurrentPage(1);

      // Initialize page assignments
      const assignments: PageAssignment[] = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        assignments.push({
          pageNumber: i,
          userId: null,
          employeeName: null,
        });
      }
      setPageAssignments(assignments);
    } catch (err: any) {
      console.error('Error loading PDF:', err);
      alert(`Error loading PDF: ${err.message}`);
      setPdfFile(null);
    }
  };

  const renderPage = async (pageNum: number) => {
    if (!pdfDoc || !canvasRef.current) return;

    const page = await pdfDoc.getPage(pageNum);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    const viewport = page.getViewport({ scale: 1.5 });
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;
  };

  const assignPageToEmployee = (pageNum: number, userId: string | null) => {
    const user = users.find((u) => u.userId === userId);
    setPageAssignments((prev) =>
      prev.map((p) =>
        p.pageNumber === pageNum
          ? {
              ...p,
              userId,
              employeeName: user
                ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
                : null,
            }
          : p
      )
    );
  };

  const extractAndUploadPayslips = async () => {
    if (!pdfFile) return;

    const assignedPages = pageAssignments.filter((p) => p.userId);
    if (assignedPages.length === 0) {
      alert('Please assign at least one page to an employee');
      return;
    }

    setIsProcessing(true);
    setUploadProgress({ current: 0, total: assignedPages.length, currentEmployee: '' });

    try {
      const originalPdfBytes = await pdfFile.arrayBuffer();

      // Group pages by employee
      const employeePages: Record<string, number[]> = {};
      assignedPages.forEach((p) => {
        if (p.userId) {
          if (!employeePages[p.userId]) {
            employeePages[p.userId] = [];
          }
          employeePages[p.userId].push(p.pageNumber);
        }
      });

      const employeeIds = Object.keys(employeePages);
      let processed = 0;

      for (const userId of employeeIds) {
        const pages = employeePages[userId];
        const user = users.find((u) => u.userId === userId);
        const employeeName = user
          ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
          : userId;

        setUploadProgress({
          current: processed + 1,
          total: employeeIds.length,
          currentEmployee: employeeName,
        });

        // Extract pages for this employee
        const originalPdf = await PDFDocument.load(originalPdfBytes);
        const newPdf = await PDFDocument.create();

        for (const pageNum of pages.sort((a, b) => a - b)) {
          const [copiedPage] = await newPdf.copyPages(originalPdf, [pageNum - 1]);
          newPdf.addPage(copiedPage);
        }

        const pdfBytes = await newPdf.save();
        const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });

        // Get presigned URL
        const fileName = `payslip-${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${employeeName.replace(/\s+/g, '_')}.pdf`;
        const uploadResponse = await payslipApi.uploadPayslip({
          userId,
          year: selectedYear,
          month: selectedMonth,
          fileName,
          contentType: 'application/pdf',
        });

        // Upload to S3
        await fetch(uploadResponse.uploadUrl, {
          method: 'PUT',
          body: blob,
          headers: {
            'Content-Type': 'application/pdf',
          },
        });

        // Confirm upload and send notification
        await payslipApi.confirmUpload({
          userId,
          year: selectedYear,
          month: selectedMonth,
        });

        processed++;
      }

      alert(`Successfully uploaded ${processed} payslip(s)`);

      // Reset state
      setPdfFile(null);
      setPdfDoc(null);
      setPageCount(0);
      setPageAssignments([]);
      setCurrentPage(1);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload status
      loadData();
    } catch (err: any) {
      console.error('Upload error:', err);
      alert(`Error uploading payslips: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setUploadProgress(null);
    }
  };

  const getUnassignedUsers = () => {
    const assignedUserIds = new Set(pageAssignments.filter((p) => p.userId).map((p) => p.userId));
    return users.filter((u) => !assignedUserIds.has(u.userId));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Payslip Management</h1>
        <p className="mt-1 text-sm text-gray-500">Upload and manage employee payslips</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadData} className="mt-2 text-sm text-red-600 underline">
            Try again
          </button>
        </div>
      ) : (
        <>
          {/* Status Summary */}
          {status && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card p-4">
                <p className="text-sm font-medium text-gray-500">Total Employees</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {status.summary.totalEmployees}
                </p>
              </div>
              <div className="card p-4">
                <p className="text-sm font-medium text-gray-500">Uploaded</p>
                <p className="mt-1 text-2xl font-semibold text-green-600">
                  {status.summary.uploadedCount}
                </p>
              </div>
              <div className="card p-4">
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="mt-1 text-2xl font-semibold text-amber-600">
                  {status.summary.pendingCount}
                </p>
              </div>
              <div className="card p-4">
                <p className="text-sm font-medium text-gray-500">Completion</p>
                <p className="mt-1 text-2xl font-semibold text-blue-600">
                  {status.summary.completionPercentage}%
                </p>
              </div>
            </div>
          )}

          {/* PDF Splitter */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Upload Payslips</h2>
                <p className="text-sm text-gray-500">
                  Uploading for: <span className="font-semibold text-blue-600">{MONTH_NAMES[selectedMonth - 1]} {selectedYear}</span>
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  {MONTH_NAMES.map((name, index) => (
                    <option key={index} value={index + 1}>
                      {name}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {!pdfFile ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="mt-4 text-sm text-gray-600">
                  Upload a combined PDF with all employee payslips
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  You can then assign each page to the correct employee
                </p>
                <label className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileSelect}
                    className="sr-only"
                  />
                  Select PDF File
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <svg className="h-8 w-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{pdfFile.name}</p>
                      <p className="text-xs text-gray-500">{pageCount} pages</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setPdfFile(null);
                      setPdfDoc(null);
                      setPageCount(0);
                      setPageAssignments([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Remove
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* PDF Preview */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-700">
                        Page {currentPage} of {pageCount}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
                          disabled={currentPage === pageCount}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="border rounded-lg overflow-auto bg-gray-100" style={{ maxHeight: '600px' }}>
                      <canvas ref={canvasRef} className="mx-auto" />
                    </div>

                    {/* Page assignment for current page */}
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assign this page to:
                      </label>
                      <select
                        value={pageAssignments[currentPage - 1]?.userId || ''}
                        onChange={(e) => assignPageToEmployee(currentPage, e.target.value || null)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                      >
                        <option value="">-- Select Employee --</option>
                        {users
                          .sort((a, b) => {
                            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
                            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email;
                            return nameA.localeCompare(nameB);
                          })
                          .map((user) => (
                            <option key={user.userId} value={user.userId}>
                              {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                              {user.department ? ` (${user.department})` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  {/* Page Assignments List */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Page Assignments</h3>
                    <div className="border rounded-lg divide-y max-h-96 overflow-auto">
                      {pageAssignments.map((assignment) => (
                        <div
                          key={assignment.pageNumber}
                          className={`p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${
                            currentPage === assignment.pageNumber ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => setCurrentPage(assignment.pageNumber)}
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-500 w-16">
                              Page {assignment.pageNumber}
                            </span>
                            {assignment.userId ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {assignment.employeeName}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Unassigned
                              </span>
                            )}
                          </div>
                          {assignment.userId && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                assignPageToEmployee(assignment.pageNumber, null);
                              }}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Summary */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">
                          {pageAssignments.filter((p) => p.userId).length}
                        </span>{' '}
                        of {pageCount} pages assigned to{' '}
                        <span className="font-medium">
                          {new Set(pageAssignments.filter((p) => p.userId).map((p) => p.userId)).size}
                        </span>{' '}
                        employee(s)
                      </p>
                      {getUnassignedUsers().length > 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          {getUnassignedUsers().length} employee(s) not yet assigned a payslip
                        </p>
                      )}
                    </div>

                    {/* Upload Button */}
                    <button
                      onClick={extractAndUploadPayslips}
                      disabled={isProcessing || pageAssignments.filter((p) => p.userId).length === 0}
                      className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProcessing ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">
                            Uploading {uploadProgress?.current} of {uploadProgress?.total}...
                          </span>
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          Upload & Send Notifications
                        </>
                      )}
                    </button>
                    {uploadProgress && (
                      <p className="mt-2 text-xs text-center text-gray-500">
                        Processing: {uploadProgress.currentEmployee}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Employee Status Table */}
          {status && (
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-medium text-gray-900">
                  {MONTH_NAMES[selectedMonth - 1]} {selectedYear} - Employee Status
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {status.employees.map((employee) => (
                      <tr key={employee.userId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{employee.employeeName}</div>
                          <div className="text-sm text-gray-500">{employee.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {employee.department || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {employee.hasPayslip ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Uploaded
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {employee.uploadedAt
                            ? new Date(employee.uploadedAt).toLocaleDateString()
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
